<script lang="ts" setup>
import { Modal } from "@/components";
import type { SharedSpaceConsentFailureReason } from "@/util";
import { onMounted, ref } from "vue";

const props = defineProps<{
  reason?: SharedSpaceConsentFailureReason;
}>();

const emits = defineEmits(["close"]);
const okButton = ref<HTMLButtonElement>();

onMounted(() => {
  okButton.value?.focus();
});

const trapFocus = (event: KeyboardEvent) => {
  if (event.key !== "Tab") {
    return;
  }

  const modal = event.currentTarget as HTMLElement | null;
  if (!modal) {
    return;
  }

  const focusable = Array.from(
    modal.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) {
    event.preventDefault();
    return;
  }

  if (
    event.shiftKey &&
    (document.activeElement === first ||
      !modal.contains(document.activeElement))
  ) {
    event.preventDefault();
    last.focus();
  } else if (
    !event.shiftKey &&
    (document.activeElement === last || !modal.contains(document.activeElement))
  ) {
    event.preventDefault();
    first.focus();
  }
};
</script>

<template>
  <Modal
    :active="true"
    role="dialog"
    aria-modal="true"
    :aria-label="
      $t(`documentSpace.missingConsent.${props.reason ?? 'missing'}.title`)
    "
    @close="emits('close')"
    @keydown="trapFocus"
  >
    <template #header>
      {{
        $t(`documentSpace.missingConsent.${props.reason ?? "missing"}.title`)
      }}
    </template>

    <template #body>
      {{
        $t(`documentSpace.missingConsent.${props.reason ?? "missing"}.message`)
      }}
    </template>

    <template #footer>
      <div class="buttons">
        <button
          ref="okButton"
          type="button"
          class="button"
          data-test="missing-consent-ok"
          @click="emits('close')"
        >
          {{ $t("ok") }}
        </button>
      </div>
    </template>
  </Modal>
</template>
